// Components
import { useMemo } from 'react';
import { Box } from '@mui/material';
import { Theme } from '@mui/material';
import { display, SystemStyleObject } from '@mui/system';
import { useEllipsis } from '@Components/shared/styles/useEllipsis';
import { TableColumnHeader } from '@Components/shared/tables/columns';
import TruncateText from '@Components/truncate-text/TruncateText';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

// Utils
import { UserListColumns, UserListFilter } from '@utils/tables/users/userListFilter';

import { handleCopyClick } from '@Components/shared/copyText/CoptyText';
import { useSnackbar } from 'notistack';

// Contracts
import { TableColumnDescription } from '@contracts/view/TableColumnDescription';
import { UserItemDto } from '@contracts/api/dto/users/UserDto';
import { Filterable } from '@contracts/common/traits/Filterable';
import { useAuthorization } from '@Modal/hooks/authorization/useAuthorization';

type HeaderOptions = {
  filter: Filterable<UserListFilter>;
};

type Column = TableColumnDescription<UserItemDto, HeaderOptions, UserListColumns>;

const useSx = () => {
  return {
    id: { width: '80px' },
  } satisfies Record<string, SystemStyleObject<Theme>>;
};

export const useUserTableColumns = () => {
  const ellipsis = useEllipsis();
  const styles = useSx();
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = useAuthorization();
  const isSuper = currentUser?.isSuper;
  return useMemo((): Column[] => {
    const columns: Column[] = [
      {
        name: 'user.id',
        title: 'ID',
        sx: { minWidth: '100px', maxWidth: '180px' },
        header: ({ title }) => <TableColumnHeader title={title} />,
        body: ({ row }) => (
          <>{row?.id && <TruncateText text={row?.id} maxLength={8} textStyles={styles.id} />}</>
        ),
      },
      {
        name: 'user.email',
        title: 'Email',
        sx: { minWidth: '150px', maxWidth: '220px' },
        header: ({ title }) => <TableColumnHeader title={title} />,
        body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.email}</Box>,
      },
      {
        name: 'user.providerId',
        title: 'Provider ID',
        sx: { minWidth: '100px', maxWidth: '130px' },
        header: ({ title }) => <TableColumnHeader title={title} />,
        body: ({ row }) => (
          <Box
            sx={{
              ...ellipsis,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            {row?.id}{' '}
            <ContentCopyIcon
              onClick={(event) => handleCopyClick(event, row?.providerId || '', enqueueSnackbar)}
            />
          </Box>
        ),
      },
      // {row?.games
      //   ?.map((game) => ProvidersTableGames[game as ProvidersTableGamesKeys])
      //   .join(', ')}
      {
        name: 'user.roleIds',
        title: 'Role IDs',
        sx: { minWidth: '250px', maxWidth: '250px' },
        header: ({ title }) => <TableColumnHeader title={title} />,
        body: ({ row }) => (
          <Box sx={{ ...ellipsis, display: 'flex', flexDirection: 'column' }}>
            {Array.isArray(row?.roleIds) ? row?.roleIds?.map((id) => id) : row?.roleIds}
          </Box>
        ),
      },
      {
        name: 'user.isProviderUsersAdmin',
        title: 'Is Provider Users Admin',
        sx: { minWidth: '250px', maxWidth: '250px' },
        header: ({ title }) => <TableColumnHeader title={title} />,
        body: ({ row }) => (
          <Box sx={{ ...ellipsis, display: 'flex', flexDirection: 'column' }}>
            {row?.isProviderUsersAdmin ? 'True' : 'False'}
          </Box>
        ),
      },
    ];

    if (isSuper) {
      columns.splice(4, 0, {
        name: 'user.providerRgs.rgsId',
        title: 'Provider RGS',
        sx: { minWidth: '120px', maxWidth: '200px' },
        header: ({ title }) => <TableColumnHeader title={title} />,
        body: ({ row }) => (
          <Box sx={{ ...ellipsis }}>{row?.providerRgs?.map((rgs) => rgs.rgsId).join(', ')}</Box>
        ),
      });
    }

    return columns;
  }, [ellipsis, isSuper]);
};
